// ─── Opportunity ──────────────────────────────────────────────────────────────

export type OpportunityMotion =
  | 'new_business_land'
  | 'renewal'
  | 'upsell'
  | 'cross_sell'
  | 'expansion'
  | 'competitive_displacement'
  | 'partner_led'
  | 'save_risk'
  | 'unknown';

export type CustomerRelationship =
  | 'net_new'
  | 'existing_customer'
  | 'former_customer'
  | 'partner_influenced'
  | 'unknown';

export const OPPORTUNITY_MOTION_LABELS: Record<OpportunityMotion, string> = {
  new_business_land:       'New Business',
  renewal:                 'Renewal',
  upsell:                  'Upsell',
  cross_sell:              'Cross-Sell',
  expansion:               'Expansion',
  competitive_displacement:'Competitive Displacement',
  partner_led:             'Partner-Led',
  save_risk:               'Save / At-Risk',
  unknown:                 'Unknown',
};

export const CUSTOMER_RELATIONSHIP_LABELS: Record<CustomerRelationship, string> = {
  net_new:             'Net New',
  existing_customer:   'Existing Customer',
  former_customer:     'Former Customer',
  partner_influenced:  'Partner Influenced',
  unknown:             'Unknown',
};

export type MeetingObjective =
  | 'discovery'
  | 'qualification'
  | 'renewal_review'
  | 'expansion_discovery'
  | 'technical_validation'
  | 'executive_alignment'
  | 'partner_strategy'
  | 'procurement_negotiation'
  | 'relationship_building'
  | 'internal_prep'
  | 'unknown';

export const MEETING_OBJECTIVE_LABELS: Record<MeetingObjective, string> = {
  discovery:                'Discovery',
  qualification:            'Qualification',
  renewal_review:           'Renewal Review',
  expansion_discovery:      'Expansion Discovery',
  technical_validation:     'Technical Validation',
  executive_alignment:      'Executive Alignment',
  partner_strategy:         'Partner Strategy',
  procurement_negotiation:  'Procurement Negotiation',
  relationship_building:    'Relationship Building',
  internal_prep:            'Internal Prep',
  unknown:                  'Unknown',
};

export type OpportunityStage =
  | 'discover'
  | 'qualify'
  | 'validate'
  | 'propose'
  | 'negotiate'
  | 'close_won'
  | 'close_lost';

export type ForecastLabel =
  | 'low'
  | 'medium'
  | 'high'
  | 'commit_risk'
  | 'commit_ready';

export type ForecastSource = 'manual' | 'mavis' | 'imported';

export interface MavisOpportunity {
  id: string;
  org_id: string;
  account_id: string;
  title: string;
  amount: number | null;       // Postgres numeric; Supabase returns as JS number
  currency: string;            // ISO 4217, default 'USD'
  stage: OpportunityStage;
  target_close_date: string | null;         // Postgres date → ISO 8601 string
  forecast_label: ForecastLabel | null;
  forecast_percentage: number | null;       // 0–100, enforced by CHECK constraint
  forecast_source: ForecastSource;
  forecast_reasoning: string;
  last_forecast_assessed_at: string | null;
  description: string;
  owner_user_id: string | null;
  created_by: string | null; // null once the creating user has been deleted
  sfdc_opportunity_id: string | null;
  sfdc_synced_at: string | null;
  created_at: string;
  updated_at: string;
  // Phase 2.7 — deal context fields
  opportunity_motion: OpportunityMotion;
  customer_relationship: CustomerRelationship;
  current_products: string[];
  target_products: string[];
  incumbent_vendor: string | null;
  competitor_in_play: string | null;
  renewal_date: string | null;
  contract_risk: string | null;
  commercial_context: Record<string, unknown>;
}

// ─── Qualification frameworks ─────────────────────────────────────────────────

export type MeddpiccKey =
  | 'metrics'
  | 'economic_buyer'
  | 'decision_criteria'
  | 'decision_process'
  | 'partners'
  | 'paper_process'
  | 'implicated_pain'
  | 'champion'
  | 'competition';

export type MeddpiccScore = 0 | 1 | 2 | 3; // 0=not assessed, 1=weak, 2=developing, 3=strong

export interface MeddpiccField {
  source?: string;
  meeting_evidence?: any[];
  status?: string;
  value: string;
  notes: string;
  score: MeddpiccScore;
}

export type MeddpiccData = Record<MeddpiccKey, MeddpiccField>;

export type NowToValueKey =
  | 'current_state'
  | 'negative_consequences'
  | 'future_state'
  | 'positive_business_outcomes'
  | 'required_capabilities'
  | 'timelines_and_milestones'
  | 'personas_required';

export interface NowToValueField {
  source?: string;
  meeting_evidence?: any[];
  status?: string;
  value: string;
  notes: string;
}

export type NowToValueData = Record<NowToValueKey, NowToValueField>;

export interface ThreeWhysData {
  why_change: string;
  why_now: string;
  why_us: string;
}

export interface MavisOpportunityQualification {
  id: string;
  org_id: string;
  opportunity_id: string;  // UNIQUE — one row per opportunity
  meddpicc: MeddpiccData;
  bant?: Record<string,{value:string;notes?:string;source?:string}>;
  now_to_value: NowToValueData;
  three_whys: ThreeWhysData;
  created_at: string;
  updated_at: string;
}

// ─── Stakeholders ─────────────────────────────────────────────────────────────

export type StakeholderRole =
  | 'unknown'
  | 'champion'
  | 'economic_buyer'
  | 'detractor'
  | 'partner'
  | 'influencer'
  | 'technical_buyer'
  | 'blocker';

export type EngagementLevel = 'strong' | 'moderate' | 'weak' | 'unknown';

export interface MavisStakeholder {
  id: string;
  org_id: string;
  opportunity_id: string;
  contact_id: string | null;  // null when not yet linked to a contacts record
  name: string;
  title: string;
  role_in_deal: StakeholderRole;
  engagement_level: EngagementLevel;
  notes: string;
  created_at: string;
  updated_at: string;
}

// ─── Knowledge sources ────────────────────────────────────────────────────────

export type KnowledgeSourceType = 'pdf' | 'text_file' | 'manual_text' | 'url';

export type KnowledgeCategory =
  | 'technical_documentation'
  | 'customer_success_story'
  | 'narrative'
  | 'pricing'
  | 'security_documentation'
  | 'product_documentation'
  | 'competitive_documentation'
  | 'call_transcript'
  | 'web_page';

export type KnowledgePriority = 'critical' | 'high' | 'medium' | 'low';

export type KnowledgeProcessStatus = 'ready' | 'processing' | 'processed' | 'error';

export interface MavisKnowledgeSource {
  id: string;
  org_id: string;
  title: string;
  source_type: KnowledgeSourceType;
  category: KnowledgeCategory;
  priority: KnowledgePriority;
  content: string | null;           // manual_text body (files and web pages are stored as chunks)
  url: string | null;               // populated for url source_type
  storage_path: string | null;      // path inside mavis-knowledge bucket
  file_name: string | null;
  file_size_bytes: number | null;
  mime_type: string | null;
  process_status: KnowledgeProcessStatus;
  error_message: string;
  is_active: boolean;
  created_by: string | null; // null once the creating user has been deleted
  created_at: string;
  updated_at: string;
}

// ─── Knowledge chunks ─────────────────────────────────────────────────────────

export interface MavisKnowledgeChunk {
  id: string;
  org_id: string;
  knowledge_source_id: string;
  chunk_index: number;   // 0-based position within the source document
  content: string;
  token_count: number | null;
  // Postgres vector(1536); Supabase JS client returns it as number[].
  // Null until mavis-embed-knowledge (Phase 2) populates it.
  embedding: number[] | null;
  created_at: string;
  // No updated_at — chunks are write-once; the source record tracks updated_at
}

// ─── Meeting sessions ─────────────────────────────────────────────────────────

export type MeetingSessionStatus =
  | 'pending'
  | 'joining'
  | 'joined'
  | 'active'
  | 'recording'
  | 'completed'
  | 'ended'
  | 'left'
  | 'failed';

export type MeetingMode = 'silent' | 'speaking' | 'background';

// Phase 4B — Controlled Voice MVP
export type VoiceMode   = 'disabled' | 'internal_prep' | 'approve_to_speak' | 'called_upon';
export type VoiceStatus = 'idle' | 'preparing' | 'speaking' | 'stopped' | 'muted' | 'error';

// Phase 4B.2 — meeting audio output capability (Recall Output Audio)
export type MeetingAudioOutputStatus = 'unconfigured' | 'configured' | 'error';

// Phase 4B.3 — real-time conversational voice (Recall Output Media)
export type RealtimeVoiceStatus = 'inactive' | 'starting' | 'active' | 'stopping' | 'error';
export type RealtimeVoiceControlAction =
  | 'start' | 'stop' | 'interrupt' | 'emergency_stop' | 'mute' | 'unmute' | 'say' | 'status';

export interface MavisMeetingSession {
  id: string;
  org_id: string;
  // opportunity_id is preferred for deal-tied meetings; account_id for pre-deal calls.
  // When both are set, account_id must match the opportunity's account_id (enforced by DB trigger).
  opportunity_id: string | null;
  account_id: string | null;
  recall_bot_id: string | null;
  meeting_url: string | null;
  meeting_title: string;
  status: MeetingSessionStatus;
  // Phase 2.1+ Recall.ai lifecycle fields
  recording_status: string;   // not_started | in_progress | done | error
  transcript_status: string;  // not_started | in_progress | done | error
  recall_bot_metadata: Record<string, unknown> | null;
  mode: MeetingMode;
  allow_mavis_to_speak: boolean;
  started_at: string | null;
  ended_at: string | null;
  transcript_storage_path: string | null;
  meeting_objective: MeetingObjective;
  // Phase 4A — Conversational MAVIS / meeting profile
  meeting_profile:              MeetingProfile;
  external_participants_present: boolean;
  mavis_conversation_mode:      MavisConversationMode;
  // Phase 4B — Controlled Voice MVP (all default OFF; existing silent behaviour unchanged)
  voice_enabled:           boolean;
  voice_mode:              VoiceMode;
  voice_status:            VoiceStatus;
  voice_muted:             boolean;
  voice_emergency_stopped: boolean;
  last_voice_action_at:    string | null;
  voice_metadata:          Record<string, unknown>;
  // Phase 4B.2 — meeting audio output capability (Recall Output Audio)
  meeting_audio_output_supported:  boolean;
  meeting_audio_output_configured: boolean;
  meeting_audio_output_status:     MeetingAudioOutputStatus;
  meeting_audio_output_error:      string | null;
  // Phase 4B.3 — real-time conversational voice (Recall Output Media)
  realtime_voice_status:     RealtimeVoiceStatus;
  realtime_voice_started_at: string | null;
  realtime_voice_error:      string | null;
  realtime_voice_supported:  boolean;
  created_by: string | null; // null once the creating user has been deleted
  created_at: string;
  updated_at: string;
}

// ─── Meeting speaker mappings ─────────────────────────────────────────────────

export type SpeakerResolutionStatus = 'auto_resolved' | 'manual_resolved' | 'unresolved' | 'ambiguous';

export interface MavisMeetingSpeakerMapping {
  id: string;
  org_id: string;
  account_id: string | null;
  opportunity_id: string | null;
  meeting_session_id: string;
  artifact_id: string | null;
  raw_speaker_label: string;
  normalized_speaker_label: string;
  recall_participant_id: string | null;
  platform_user_id: string | null;
  participant_name: string | null;
  participant_email: string | null;
  contact_id: string | null;
  resolution_status: SpeakerResolutionStatus;
  resolution_confidence: number | null;
  resolution_source: string | null;
  metadata: Record<string, unknown>;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

// ─── Meeting artifacts ────────────────────────────────────────────────────────

export type ArtifactType = 'transcript' | 'recording' | 'audio' | 'video' | 'chat' | 'metadata';
export type ArtifactStatus = 'pending' | 'processing' | 'ready' | 'failed';

export interface MavisMeetingArtifact {
  id: string;
  org_id: string;
  account_id: string | null;
  opportunity_id: string | null;
  meeting_session_id: string;
  artifact_type: ArtifactType;
  provider: string;
  provider_artifact_id: string | null;
  provider_url: string | null;
  storage_path: string | null;
  status: ArtifactStatus;
  content_text: string | null;
  content_json: unknown;
  mime_type: string | null;
  duration_seconds: number | null;
  file_size_bytes: number | null;
  metadata: Record<string, unknown>;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

// ─── KB config ────────────────────────────────────────────────────────────────

export type DefaultQualificationFramework =
  | 'meddpicc'
  | 'now_to_value'
  | 'three_whys'
  | 'both'   // MEDDPICC + Now to Value shown together
  | 'all';   // All three frameworks shown

export interface MavisKbConfig {
  id: string;
  org_id: string;  // UNIQUE — one row per org
  knowledge_base_enabled: boolean;
  default_qualification_framework: DefaultQualificationFramework;
  include_vendor_intel_in_context: boolean;
  include_battlecards_in_context: boolean;
  // Automatic AI analysis of captured meetings (live playbook); off by default.
  live_insights_enabled?: boolean;
  // Whether live insights may create contacts/stakeholders; off by default.
  live_insights_crm_writeback?: boolean;
  created_at: string;
  updated_at: string;
}

// ─── MAVIS sessions (AI chat history) ────────────────────────────────────────

export type MavisSessionType = 'chat' | 'prep' | 'debrief';

export interface MavisSessionMessage {
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
}

export interface MavisSession {
  id: string;
  org_id: string;
  user_id: string;
  account_id: string | null;
  opportunity_id: string | null;
  session_type: MavisSessionType;
  title: string;
  messages: MavisSessionMessage[];  // JSONB array; edge function caps at ~20 turns
  created_at: string;
  updated_at: string;
}

// ─── Label maps ───────────────────────────────────────────────────────────────

export const OPPORTUNITY_STAGE_LABELS: Record<OpportunityStage, string> = {
  discover:   'Discover',
  qualify:    'Qualify',
  validate:   'Validate',
  propose:    'Propose',
  negotiate:  'Negotiate',
  close_won:  'Closed Won',
  close_lost: 'Closed Lost',
};

export const FORECAST_LABEL_LABELS: Record<ForecastLabel, string> = {
  low:          'Low',
  medium:       'Medium',
  high:         'High',
  commit_risk:  'Commit Risk',
  commit_ready: 'Commit Ready',
};

export const KNOWLEDGE_CATEGORY_LABELS: Record<KnowledgeCategory, string> = {
  technical_documentation:  'Technical Documentation',
  customer_success_story:   'Customer Success Story',
  narrative:                'Narrative',
  pricing:                  'Pricing',
  security_documentation:   'Security Documentation',
  product_documentation:    'Product Documentation',
  competitive_documentation: 'Competitive Documentation',
  call_transcript:          'Call Transcript',
  web_page:                 'Web Page',
};

export const STAKEHOLDER_ROLE_LABELS: Record<StakeholderRole, string> = {
  unknown: 'Role unconfirmed',
  champion:       'Champion',
  economic_buyer: 'Economic Buyer',
  detractor:      'Detractor',
  partner:        'Partner',
  influencer:     'Influencer',
  technical_buyer: 'Technical Buyer',
  blocker:        'Blocker',
};

export const MEDDPICC_KEY_LABELS: Record<MeddpiccKey, string> = {
  metrics:           'Metrics',
  economic_buyer:    'Economic Buyer',
  decision_criteria: 'Decision Criteria',
  decision_process:  'Decision Process',
  paper_process: 'Paper Process',
  partners:          'Partners',
  implicated_pain:   'Implicated Pain',
  champion:          'Champion',
  competition:       'Competition',
};

export const NOW_TO_VALUE_KEY_LABELS: Record<NowToValueKey, string> = {
  current_state:              'Current State',
  negative_consequences:      'Negative Consequences',
  future_state:               'Future State',
  positive_business_outcomes: 'Positive Business Outcomes',
  required_capabilities:      'Required Capabilities',
  timelines_and_milestones:   'Timelines & Milestones',
  personas_required:          'Personas Required',
};

export const MEDDPICC_SCORE_LABELS: Record<MeddpiccScore, string> = {
  0: 'Not assessed',
  1: 'Weak',
  2: 'Developing',
  3: 'Strong',
};

// ─── Opportunity Prep Brief (Phase 2.1) ──────────────────────────────────────

export type DealHealth = 'strong' | 'moderate' | 'weak' | 'critical';

export interface PrepBriefQualificationGap {
  field: string;
  severity: 'high' | 'medium' | 'low';
  recommendation: string;
}

export interface PrepBriefStakeholderRisk {
  name: string;
  risk: string;
  action: string;
}

export interface PrepBriefNextAction {
  action: string;
  rationale: string;
}

export interface MavisOpportunityPrepBrief {
  executive_summary: string;
  deal_health: DealHealth;
  deal_health_reasoning: string;
  forecast_assessment: string;
  qualification_gaps: PrepBriefQualificationGap[];
  stakeholder_risks: PrepBriefStakeholderRisk[];
  recommended_questions: string[];
  recommended_next_actions: PrepBriefNextAction[];
  meeting_prep_notes: string;
  confidence: number;         // 0–100
  reasoning_notes: string;
  apply_suggestions?: ApplySuggestions;
}

// ─── Apply Suggestions (Phase 2.3) ───────────────────────────────────────────

export interface ApplySuggestionField {
  suggested_value: string;
  reason: string;
  confidence: number;
}

export interface MeddpiccApplySuggestions {
  metrics?: ApplySuggestionField;
  economic_buyer?: ApplySuggestionField;
  decision_criteria?: ApplySuggestionField;
  decision_process?: ApplySuggestionField;
  partners?: ApplySuggestionField;
  implicated_pain?: ApplySuggestionField;
  champion?: ApplySuggestionField;
  competition?: ApplySuggestionField;
}

export interface NowToValueApplySuggestions {
  current_state?: ApplySuggestionField;
  negative_consequences?: ApplySuggestionField;
  future_state?: ApplySuggestionField;
  positive_business_outcomes?: ApplySuggestionField;
  required_capabilities?: ApplySuggestionField;
  timelines_and_milestones?: ApplySuggestionField;
  personas_required?: ApplySuggestionField;
}

export interface ThreeWhysApplySuggestions {
  why_change?: ApplySuggestionField;
  why_now?: ApplySuggestionField;
  why_us?: ApplySuggestionField;
}

export interface ForecastUpdateSuggestion {
  forecast_label: string;
  forecast_percentage: number;
  reason: string;
  confidence: number;
}

export interface StakeholderSuggestion {
  name: string;
  title: string;
  role_in_deal: string;
  engagement_level: string;
  reason: string;
  confidence: number;
}

export interface NextActionSuggestion {
  action: string;
  rationale: string;
  priority: string;
  suggested_owner: string;
  suggested_timing: string;
}

export interface ApplySuggestions {
  qualification_updates?: {
    meddpicc?: MeddpiccApplySuggestions;
    now_to_value?: NowToValueApplySuggestions;
    three_whys?: ThreeWhysApplySuggestions;
  };
  forecast_update?: ForecastUpdateSuggestion;
  stakeholder_suggestions?: StakeholderSuggestion[];
  next_actions?: NextActionSuggestion[];
}

export interface MavisOpportunityPrepBriefRow {
  id: string;
  org_id: string;
  account_id: string;
  opportunity_id: string;
  generated_by: string | null;
  brief_json: MavisOpportunityPrepBrief;
  executive_summary: string | null;
  deal_health: string | null;
  forecast_label_recommendation: string | null;
  forecast_percentage_recommendation: number | null;
  confidence: number | null;
  model: string | null;
  prompt_slug: string;
  created_at: string;
}

// ─── Pre-Call Brief (Phase 2.7) ──────────────────────────────────────────────

export interface PreCallQualificationGap {
  framework: string;
  gap: string;
  why_it_matters: string;
  question_to_ask: string;
}

export interface PreCallUseCaseToValidate {
  use_case_id: string | null;
  use_case_name: string;
  relevance: 'high' | 'medium' | 'low';
  why_relevant: string;
  validation_questions: string[];
}

export interface PreCallStakeholderStrategy {
  stakeholder_or_role: string;
  risk_or_opportunity: string;
  recommended_approach: string;
}

export interface PreCallSuggestedQuestion {
  question: string;
  purpose: string;
  priority: 'high' | 'medium' | 'low';
}

export interface PreCallCommercialAngle {
  angle: string;
  when_to_use: string;
  risk: string;
}

export interface PreCallStep {
  step: string;
  talk_track: string;
}

export interface MavisPreCallBrief {
  meeting_objective: string;
  opportunity_motion: string;
  commercial_context_summary: string;
  what_we_know: string[];
  what_we_do_not_know: string[];
  qualification_gaps: PreCallQualificationGap[];
  use_cases_to_validate: PreCallUseCaseToValidate[];
  stakeholder_strategy: PreCallStakeholderStrategy[];
  suggested_questions: PreCallSuggestedQuestion[];
  commercial_angles: PreCallCommercialAngle[];
  do_not_assume: string[];
  recommended_call_plan: PreCallStep[];
  confidence: number;
  limitations: string[];
}

export interface MavisPreCallBriefRow {
  id: string;
  org_id: string;
  account_id: string | null;
  opportunity_id: string | null;
  meeting_session_id: string;
  brief_json: MavisPreCallBrief;
  model: string | null;
  prompt_slug: string;
  confidence: number | null;
  generated_by: string | null;
  generated_at: string | null;
  created_at: string;
  updated_at: string;
}

// ─── MAVIS Forecast Assessment (Phase 1B.5) ──────────────────────────────────

export interface MavisForecastRisk {
  risk: string;
  severity: 'High' | 'Medium' | 'Low';
  mitigation: string;
}

export interface MavisNextBestAction {
  action: string;
  priority: 'High' | 'Medium' | 'Low';
  rationale: string;
}

export interface MavisForecastAssessment {
  opportunity_strength_score: number;
  qualification_score: number;
  forecast_label: string;
  forecast_percentage: number;
  summary: string;
  evidence_summary: string;
  risks: MavisForecastRisk[];
  next_best_actions: MavisNextBestAction[];
  forecast_timing_guidance: string;
  confidence: number;
  context_gaps: string[];
}

export interface MavisForecastAssessmentRow extends MavisForecastAssessment {
  id: string;
  org_id: string;
  account_id: string;
  opportunity_id: string;
  created_by: string | null;
  model: string | null;
  prompt_slug: string;
  created_at: string;
}

// ─── MAVIS Deal Coach Chat (Phase 1B.4) ──────────────────────────────────────

export interface MavisDealCoachResponse {
  answer: string;
  why_it_matters: string;
  recommended_next_steps: string[];
  questions_to_ask: string[];
  risks_and_assumptions: string[];
  confidence: number;
  context_gaps: string[];
}

// ─── Meeting Summary (Phase 2.4) ─────────────────────────────────────────────

export type MeetingSummaryStatus = 'draft' | 'generated' | 'failed';

// ─── Meeting Relevance & Evidence Classification (Phase 2.4.1) ────────────────

export type MeetingType =
  | 'customer_meeting'
  | 'internal_prep'
  | 'partner_discussion'
  | 'discovery'
  | 'follow_up'
  | 'relationship_building'
  | 'background_noise'
  | 'mixed'
  | 'unknown';

export type RelevanceLevel = 'high' | 'medium' | 'low' | 'none';

export type EvidenceStrength =
  | 'validated_customer_evidence'
  | 'external_stakeholder_signal'
  | 'seller_observation'
  | 'internal_hypothesis'
  | 'inferred_context'
  | 'background_noise';

export interface MavisMeetingClassification {
  meeting_type:                             MeetingType;
  customer_or_external_stakeholder_present: boolean;
  internal_only:                            boolean;
  direct_opportunity_relevance:             RelevanceLevel;
  account_relevance:                        RelevanceLevel;
  use_case_relevance:                       RelevanceLevel;
  evidence_strength:                        EvidenceStrength;
  should_update_opportunity:                boolean;
  should_create_follow_up_actions:          boolean;
  should_enrich_account_strategy:           boolean;
  reasoning:                                string;
}

export interface MavisSummaryUseCaseAlignment {
  use_case_id:      string | null;
  use_case_name:    string;
  alignment:        'supports' | 'weakens' | 'introduces' | 'unrelated';
  relevance:        RelevanceLevel;
  evidence_strength: 'validated' | 'inferred' | 'internal_hypothesis' | 'background';
  reasoning:        string;
}

export interface MavisSummaryAccountStrategyInsight {
  insight:            string;
  source:             string;
  relevance:          RelevanceLevel;
  confidence:         number;
  recommended_action: string;
}

export interface MavisSummaryOpportunityEvidenceGate {
  can_be_used_for_qualification_updates: boolean;
  blocked_reason:      string | null;
  safe_to_use_for:     string[];
  not_safe_to_use_for: string[];
}

export interface MavisSummaryAttendee {
  name: string;
  email: string | null;
  role: string | null;
  speaker_label: string;
  participation_summary: string;
}

export interface MavisSummaryKeyTopic {
  topic: string;
  summary: string;
  participants: string[];
}

export interface MavisSummaryCustomerPain {
  pain: string;
  who_said_it: string;
  evidence: string;
}

export interface MavisSummaryBusinessOutcome {
  outcome: string;
  who_said_it: string;
  evidence: string;
}

export interface MavisSummaryObjectionOrRisk {
  risk: string;
  raised_by: string;
  severity: 'low' | 'medium' | 'high';
  evidence: string;
}

export interface MavisSummaryCompetitor {
  name: string;
  context: string;
}

export interface MavisSummaryDecision {
  decision: string;
  owner: string;
  evidence: string;
}

export interface MavisSummaryNextStep {
  action: string;
  owner: string;
  due_date: string | null;
  source: 'customer' | 'seller' | 'mavis_inferred';
}

export interface MavisSummaryQualificationSignals {
  meddpicc:    Record<string, string[]>;
  now_to_value: Record<string, string[]>;
  three_whys:  Record<string, string[]>;
}

export interface MavisSummaryMavisNotes {
  what_changed_since_last_context: string[];
  open_questions:                  string[];
  recommended_follow_up:           string[];
}

export interface MavisMeetingSummaryJson {
  executive_summary:                    string;
  meeting_purpose:                      string;
  attendees:                            MavisSummaryAttendee[];
  key_topics:                           MavisSummaryKeyTopic[];
  customer_pains:                       MavisSummaryCustomerPain[];
  business_outcomes_discussed:          MavisSummaryBusinessOutcome[];
  objections_or_risks:                  MavisSummaryObjectionOrRisk[];
  competitors_or_alternatives_mentioned: MavisSummaryCompetitor[];
  decisions_made:                       MavisSummaryDecision[];
  next_steps:                           MavisSummaryNextStep[];
  qualification_signals:                MavisSummaryQualificationSignals;
  follow_up_email_points:               string[];
  mavis_notes:                          MavisSummaryMavisNotes;
  confidence:                           number;
  limitations:                          string[];
  // Phase 2.4.1 — Relevance & Evidence Classification
  meeting_classification:    MavisMeetingClassification | null;
  use_case_alignment:        MavisSummaryUseCaseAlignment[];
  account_strategy_insights: MavisSummaryAccountStrategyInsight[];
  opportunity_evidence_gate: MavisSummaryOpportunityEvidenceGate | null;
}

export interface MavisMeetingSummary {
  id:                     string;
  org_id:                 string;
  account_id:             string | null;
  opportunity_id:         string | null;
  meeting_session_id:     string;
  transcript_artifact_id: string | null;
  summary_status:         MeetingSummaryStatus;
  summary_text:           string | null;
  summary_json:           MavisMeetingSummaryJson | Record<string, unknown>;
  model:                  string | null;
  prompt_slug:            string;
  confidence:             number | null;
  error_message:          string | null;
  generated_by:           string | null;
  generated_at:           string | null;
  created_at:             string;
  updated_at:             string;
}

// ─── Composite helpers for React components ───────────────────────────────────

// Opportunity workspace: opportunity + its one-to-one qualification record
export interface MavisOpportunityWithQualification extends MavisOpportunity {
  qualification: MavisOpportunityQualification | null;
}

// Stakeholder map: row with optional joined contact details
export interface MavisStakeholderWithContact extends MavisStakeholder {
  contact_email?: string;
  contact_linkedin_url?: string;
}

// Supabase joined row: opportunity + nested account fields (from .select('*, accounts(...)'))
export interface MavisOpportunityRow extends MavisOpportunity {
  accounts: { company_name: string; logo_url: string | null } | null;
}

// ─── Meeting Qualification Suggestions (Phase 2.5) ────────────────────────────

export type QualSuggestionAction = 'add' | 'replace' | 'reinforce' | 'no_change';
export type MavisQualSuggestionStatus = 'generated' | 'partially_applied' | 'applied' | 'dismissed' | 'failed';

export interface MavisQualFieldSuggestion {
  suggested_value:  string;
  evidence:         string;
  who_said_it:      string;
  evidence_strength: EvidenceStrength;
  confidence:       number;
  action:           QualSuggestionAction;
}

export interface MavisStakeholderSuggestionV2 {
  name:          string;
  email:         string | null;
  suggested_role: string;
  evidence:      string;
  confidence:    number;
  action:        'add' | 'update' | 'no_change';
}

export interface MavisNextActionSuggestionV2 {
  action:     string;
  owner:      string;
  source:     'meeting_summary' | 'transcript' | 'mavis_inferred';
  confidence: number;
}

export interface MavisForecastImplication {
  should_change_forecast: boolean;
  suggested_label:       string | null;
  suggested_percentage:  number | null;
  reasoning:             string;
  confidence:            number;
}

export interface SellerObservationDraftField {
  framework:                     'MEDDPICC' | 'Now to Value' | 'Three Whys' | 'Stakeholder' | 'Commercial';
  field_key:                     string;
  field_label:                   string;
  suggested_value:               string;
  evidence_summary:              string;
  source_type:                   'seller_observation';
  evidence_strength:             'weak';
  validation_status:             'unvalidated';
  confidence_score:              number;
  needs_customer_validation:     true;
  suggested_validation_question: string;
  source_meeting_session_id?:    string;
  source_summary_id?:            string | null;
  source_event_ids?:             string[];
}

export interface SellerObsDraftSummary {
  total_fields_extracted: number;
  frameworks_covered:     string[];
  key_gaps_to_validate:   string[];
}

export interface MavisQualSuggestionsJson {
  can_generate_qualification_suggestions: boolean;
  blocked_reason?:         string;
  evidence_gate_reasoning?: string;
  meddpicc_suggestions?: {
    metrics?:           MavisQualFieldSuggestion[];
    economic_buyer?:    MavisQualFieldSuggestion[];
    decision_criteria?: MavisQualFieldSuggestion[];
    decision_process?:  MavisQualFieldSuggestion[];
    partners?:          MavisQualFieldSuggestion[];
    implicated_pain?:   MavisQualFieldSuggestion[];
    champion?:          MavisQualFieldSuggestion[];
    competition?:       MavisQualFieldSuggestion[];
  };
  now_to_value_suggestions?: {
    current_state?:                MavisQualFieldSuggestion[];
    negative_consequences?:        MavisQualFieldSuggestion[];
    future_state?:                 MavisQualFieldSuggestion[];
    positive_business_outcomes?:   MavisQualFieldSuggestion[];
    required_capabilities?:        MavisQualFieldSuggestion[];
    timelines_and_milestones?:     MavisQualFieldSuggestion[];
    personas_required?:            MavisQualFieldSuggestion[];
  };
  three_whys_suggestions?: {
    why_change?: MavisQualFieldSuggestion[];
    why_now?:    MavisQualFieldSuggestion[];
    why_us?:     MavisQualFieldSuggestion[];
  };
  stakeholder_suggestions?:   MavisStakeholderSuggestionV2[];
  next_action_suggestions?:   MavisNextActionSuggestionV2[];
  forecast_implications?:     MavisForecastImplication;
  safe_suggestions?: {
    account_strategy_actions?: string[];
    follow_up_actions?:        string[];
  };
  not_safe_to_update?: string[];
  warnings?:           string[];
  confidence:          number;
  seller_observation_qualification_draft?: SellerObservationDraftField[];
  seller_obs_draft_summary?:               SellerObsDraftSummary;
}

export interface MavisMeetingQualSuggestion {
  id:                 string;
  org_id:             string;
  account_id:         string | null;
  opportunity_id:     string | null;
  meeting_session_id: string;
  meeting_summary_id: string | null;
  suggestion_status:  MavisQualSuggestionStatus;
  suggestions_json:   MavisQualSuggestionsJson;
  model:              string | null;
  prompt_slug:        string;
  confidence:         number | null;
  error_message:      string | null;
  generated_by:       string | null;
  generated_at:       string | null;
  created_at:         string;
  updated_at:         string;
}

// ─── Meeting Intelligence State (Phase 2.6) ───────────────────────────────────
// Fully derived from existing data — no new DB columns required.

export type MavisIntelligenceState =
  | 'waiting_for_meeting'           // active session or no bot attached yet
  | 'waiting_for_transcript'        // no transcript artifact or still pending
  | 'processing_transcript'         // transcript artifact status=processing
  | 'speaker_resolution_required'   // transcript ready, speakers unresolved
  | 'ready_to_process'              // transcript+speakers ready, no summary yet
  | 'processing'                    // orchestrator is running (frontend-only state)
  | 'generating_summary'            // individual summary regeneration in flight
  | 'generating_qualification_suggestions' // individual qual regen in flight
  | 'ready_for_review'              // all done, evidence gate passed
  | 'blocked_by_evidence_gate'      // all done, evidence gate blocked
  | 'failed';                       // any pipeline step errored

// ── Phase 3.1: Live meeting events ───────────────────────────────────────────

// Normalized event types stored in mavis_live_meeting_events.
// Must match LIVE_EVENT_TYPES in src/types/mavis-realtime.ts and the webhook ingestion functions.
export type LiveEventType =
  // Transcript (Phase 3.3 realtime webhook)
  | 'transcript_segment'   // Recall transcript.data — finalized utterance
  | 'transcript_partial'   // Recall transcript.partial_data — in-progress
  // Participant presence
  | 'participant_join'
  | 'participant_leave'
  | 'participant_update'
  | 'speech_on'
  | 'speech_off'
  // Bot lifecycle (from mavis-refresh-live-meeting-events status events)
  | 'bot_ready'
  | 'bot_joining'
  | 'bot_joined'
  | 'bot_recording'
  | 'bot_waiting'
  | 'bot_left'
  | 'bot_error'
  | 'call_ended'
  | 'recording_denied'
  // Legacy / fallback
  | 'system'
  | (string & Record<never, never>); // allow unknown future types without breaking

export interface MavisLiveEvent {
  id:                 string;
  org_id:             string;
  account_id:         string | null;
  opportunity_id:     string | null;
  meeting_session_id: string;
  event_type:         LiveEventType;
  speaker_label:      string | null;
  participant_name:   string | null;
  participant_email:  string | null;
  content_text:       string | null;
  content_json:       Record<string, unknown>;
  source:             string;
  provider_event_id:  string | null;
  event_timestamp:    string | null;
  created_at:         string;
}

// ── Phase 3.2: Live Guidance ──────────────────────────────────────────────────

export type GuidanceType =
  | 'suggested_question'
  | 'qualification_gap'
  | 'risk_alert'
  | 'objection_detected'
  | 'competitor_mention'
  | 'stakeholder_signal'
  | 'decision_process_signal'
  | 'follow_up_action'
  | 'do_not_assume'
  | 'commercial_angle'
  | 'next_step_capture'
  | 'system';

export type GuidancePriority = 'low' | 'medium' | 'high' | 'critical';
export type GuidanceStatus   = 'active' | 'dismissed' | 'accepted' | 'expired';

export const GUIDANCE_TYPE_LABELS: Record<GuidanceType, string> = {
  suggested_question:       'Suggested Question',
  qualification_gap:        'Qualification Gap',
  risk_alert:               'Risk Alert',
  objection_detected:       'Objection',
  competitor_mention:       'Competitor Signal',
  stakeholder_signal:       'Stakeholder Signal',
  decision_process_signal:  'Decision Process',
  follow_up_action:         'Follow-up Action',
  do_not_assume:            'Do Not Assume',
  commercial_angle:         'Commercial Angle',
  next_step_capture:        'Next Step',
  system:                   'System',
};

export const GUIDANCE_TYPE_COLORS: Record<GuidanceType, string> = {
  suggested_question:       'bg-blue-50 text-blue-700 border-blue-200',
  qualification_gap:        'bg-amber-50 text-amber-700 border-amber-200',
  risk_alert:               'bg-red-50 text-red-700 border-red-200',
  objection_detected:       'bg-orange-50 text-orange-700 border-orange-200',
  competitor_mention:       'bg-purple-50 text-purple-700 border-purple-200',
  stakeholder_signal:       'bg-teal-50 text-teal-700 border-teal-200',
  decision_process_signal:  'bg-indigo-50 text-indigo-700 border-indigo-200',
  follow_up_action:         'bg-green-50 text-green-700 border-green-200',
  do_not_assume:            'bg-gray-50 text-gray-600 border-gray-200',
  commercial_angle:         'bg-emerald-50 text-emerald-700 border-emerald-200',
  next_step_capture:        'bg-violet-50 text-violet-700 border-violet-200',
  system:                   'bg-gray-50 text-gray-500 border-gray-200',
};

export interface MavisLiveGuidanceEvent {
  id:                 string;
  org_id:             string;
  account_id:         string | null;
  opportunity_id:     string | null;
  meeting_session_id: string;
  guidance_type:      GuidanceType;
  priority:           GuidancePriority;
  title:              string;
  message:            string;
  suggested_action:   string | null;
  related_framework:  string | null;
  related_field:      string | null;
  speaker_name:       string | null;
  evidence:           string | null;
  confidence:         number | null;
  source_event_ids:   string[] | null;
  status:             GuidanceStatus;
  metadata:           Record<string, unknown>;
  created_at:         string;
  updated_at:         string;
}

// ── Phase 3.6: Live Qualification Tracking ───────────────────────────────────

export type LiveQualCoverageStatus =
  | 'missing'
  | 'mentioned'
  | 'weak_signal'
  | 'partially_covered'
  | 'validated'
  | 'contradicted';

export type LiveQualEvidenceStrength =
  | 'none'
  | 'seller_observation'
  | 'internal_hypothesis'
  | 'customer_signal'
  | 'validated_customer_evidence';

export type LiveQualFramework =
  | 'MEDDPICC'
  | 'Now to Value'
  | 'Three Whys'
  | 'Stakeholder'
  | 'Commercial';

export interface MavisLiveQualTrackingRow {
  id:                 string;
  org_id:             string;
  account_id:         string | null;
  opportunity_id:     string | null;
  meeting_session_id: string;
  framework:          LiveQualFramework;
  field_key:          string;
  field_label:        string;
  coverage_status:    LiveQualCoverageStatus;
  evidence_strength:  LiveQualEvidenceStrength;
  evidence:           string | null;
  speaker_name:       string | null;
  source_event_ids:   string[];
  confidence:         number | null;
  suggested_question: string | null;
  updated_by:         'live_ai' | 'manual';
  metadata:           Record<string, unknown>;
  created_at:         string;
  updated_at:         string;
}

export const LIVE_QUAL_COVERAGE_LABELS: Record<LiveQualCoverageStatus, string> = {
  missing:          'Missing',
  mentioned:        'Mentioned',
  weak_signal:      'Weak Signal',
  partially_covered:'Partially Covered',
  validated:        'Validated',
  contradicted:     'Contradicted',
};

export const LIVE_QUAL_COVERAGE_COLORS: Record<LiveQualCoverageStatus, string> = {
  missing:          'bg-gray-100 text-gray-400 border-gray-200',
  mentioned:        'bg-amber-50 text-amber-600 border-amber-200',
  weak_signal:      'bg-amber-50 text-amber-700 border-amber-200',
  partially_covered:'bg-blue-50 text-blue-700 border-blue-200',
  validated:        'bg-emerald-50 text-emerald-700 border-emerald-200',
  contradicted:     'bg-red-50 text-red-700 border-red-200',
};

export const LIVE_QUAL_EVIDENCE_LABELS: Record<LiveQualEvidenceStrength, string> = {
  none:                     'No evidence',
  seller_observation:       'Seller',
  internal_hypothesis:      'Inferred',
  customer_signal:          'Customer signal',
  validated_customer_evidence: 'Customer validated',
};

export const LIVE_QUAL_EVIDENCE_COLORS: Record<LiveQualEvidenceStrength, string> = {
  none:                     'bg-gray-100 text-gray-400',
  seller_observation:       'bg-amber-50 text-amber-600',
  internal_hypothesis:      'bg-gray-100 text-gray-500',
  customer_signal:          'bg-blue-50 text-blue-700',
  validated_customer_evidence: 'bg-emerald-50 text-emerald-700',
};

// ── Phase 3.7 — Live Response Drafts ─────────────────────────────────────────

export type ResponseDraftType =
  | 'discovery_question'
  | 'qualification_question'
  | 'objection_response'
  | 'clarification'
  | 'closeout_question'
  | 'next_step_confirmation'
  | 'value_framing'
  | 'executive_framing'
  | 'technical_validation'
  | 'commercial_process'
  | 'customer_safe_answer'
  | 'do_not_say';

export type ResponseDraftStatus   = 'active' | 'copied' | 'used' | 'dismissed' | 'expired';
export type ResponseDraftPriority = 'critical' | 'high' | 'medium' | 'low';
export type RecommendedDelivery   = 'seller_says' | 'mavis_can_say_later' | 'private_only';

export interface MavisLiveResponseDraft {
  id:                   string;
  org_id:               string;
  account_id:           string | null;
  opportunity_id:       string;
  meeting_session_id:   string;
  draft_type:           ResponseDraftType;
  priority:             ResponseDraftPriority;
  title:                string;
  response_text:        string;
  rationale:            string | null;
  related_framework:    string | null;
  related_field_key:    string | null;
  evidence_summary:     string | null;
  source_event_ids:     string[];
  confidence:           number | null;
  status:               ResponseDraftStatus;
  tone:                 string | null;
  // Voice readiness metadata (Phase 4.3)
  speakable:            boolean;
  requires_approval:    boolean;
  voice_safe:           boolean;
  unsafe_reason:        string | null;
  recommended_delivery: RecommendedDelivery;
  metadata:             Record<string, unknown>;
  created_at:           string;
  updated_at:           string;
}

export const RESPONSE_DRAFT_TYPE_LABELS: Record<ResponseDraftType, string> = {
  discovery_question:     'Discovery',
  qualification_question: 'Qualification',
  objection_response:     'Objection',
  clarification:          'Clarification',
  closeout_question:      'Closeout',
  next_step_confirmation: 'Next Step',
  value_framing:          'Value Frame',
  executive_framing:      'Exec Frame',
  technical_validation:   'Technical',
  commercial_process:     'Commercial',
  customer_safe_answer:   'Safe Answer',
  do_not_say:             'Do Not Say',
};

export const RESPONSE_DRAFT_PRIORITY_ORDER: Record<ResponseDraftPriority, number> = {
  critical: 0,
  high:     1,
  medium:   2,
  low:      3,
};

// ── Phase 4A — Conversational MAVIS / Meeting Profile ────────────────────────

export type MeetingProfile =
  | 'internal_prep'
  | 'external_customer'
  | 'partner'
  | 'mixed'
  | 'unknown';

export type MavisConversationMode =
  | 'silent'
  | 'private_guidance'
  | 'private_ask'
  | 'draft_response'
  | 'future_approve_to_speak'
  | 'disabled';

export const MEETING_PROFILE_LABELS: Record<MeetingProfile, string> = {
  internal_prep:     'Internal Prep',
  external_customer: 'External Customer',
  partner:           'Partner',
  mixed:             'Mixed',
  unknown:           'Unknown',
};

export const MEETING_PROFILE_DESCRIPTIONS: Record<MeetingProfile, string> = {
  internal_prep:     'Conversational prep mode — MAVIS can help you think, rehearse, and challenge assumptions.',
  external_customer: 'Guardrailed mode — MAVIS answers privately and keeps customer-safe wording.',
  partner:           'Guardrailed mode — MAVIS answers privately for partner-facing conversations.',
  mixed:             'Mixed audience — MAVIS defaults to guardrailed customer-safe mode.',
  unknown:           'Profile unknown — MAVIS defaults to guardrailed mode.',
};

export type ConversationAnswerType =
  | 'strategic'
  | 'qualification'
  | 'knowledge'
  | 'summary'
  | 'objection'
  | 'next_step'
  | 'safe_deferral';

export type ConversationGroundingStatus =
  | 'grounded'
  | 'partially_grounded'
  | 'unsupported_deferral'
  | 'not_required';

// Phase 4A.1 — Evidence-backed answers

export type EvidenceSourceType =
  | 'transcript'
  | 'live_event'
  | 'meeting_summary'
  | 'live_guidance'
  | 'qualification_tracker'
  | 'approved_knowledge'
  | 'seller_observation'
  | 'user_correction'
  | 'inference';

export type EvidenceStrengthLevel =
  | 'none'
  | 'seller_observation'
  | 'internal_hypothesis'
  | 'customer_signal'
  | 'validated_customer_evidence'
  | 'approved_knowledge'
  | 'user_confirmed';

export interface MavisEvidenceItem {
  source_type:         EvidenceSourceType;
  source_id?:          string;
  meeting_session_id?: string;
  speaker_name?:       string;
  speaker_role?:       string;
  event_timestamp?:    string;
  created_at?:         string;
  quote?:              string;
  summary?:            string;
  evidence_strength:   EvidenceStrengthLevel;
  confidence?:         number;
  relevance_reason?:   string;
}

export type CorrectionType =
  | 'factual'
  | 'qualification'
  | 'stakeholder'
  | 'timeline'
  | 'knowledge'
  | 'other';

export type CorrectionMemoryStatus = 'pending' | 'saved' | 'dismissed' | 'superseded';

export interface MavisConversationCorrection {
  id:                       string;
  org_id:                   string;
  account_id:               string | null;
  opportunity_id:           string;
  meeting_session_id:       string | null;
  conversation_message_id:  string | null;
  user_id:                  string | null;
  correction_type:          CorrectionType;
  original_answer:          string | null;
  correction_text:          string;
  save_as_memory:           boolean;
  memory_status:            CorrectionMemoryStatus;
  applied_to_qualification: boolean;
  applied_to_knowledge:     boolean;
  confidence:               number;
  metadata:                 Record<string, unknown>;
  created_at:               string;
}

export const CORRECTION_TYPE_LABELS: Record<CorrectionType, string> = {
  factual:       'Factual correction',
  qualification: 'Qualification correction',
  stakeholder:   'Stakeholder correction',
  timeline:      'Timeline correction',
  knowledge:     'Knowledge correction',
  other:         'Other',
};

export const EVIDENCE_SOURCE_LABELS: Record<EvidenceSourceType, string> = {
  transcript:             'Transcript',
  live_event:             'Live Event',
  meeting_summary:        'Summary',
  live_guidance:          'Guidance',
  qualification_tracker:  'Qual Tracker',
  approved_knowledge:     'Knowledge',
  seller_observation:     'Seller Obs.',
  user_correction:        'User Correction',
  inference:              'Inference',
};

export const EVIDENCE_STRENGTH_LABELS: Record<EvidenceStrengthLevel, string> = {
  validated_customer_evidence: 'Customer said it',
  customer_signal:             'Customer signal',
  approved_knowledge:          'Approved knowledge',
  user_confirmed:              'User correction',
  seller_observation:          'Seller observation',
  internal_hypothesis:         'Internal assumption',
  none:                        'Inference',
};

export interface MavisConversationSource {
  source_id: string;
  title:     string;
  category:  string;
  priority:  string;
  snippet:   string;
}

export interface MavisConversationResponse {
  ok:                    boolean;
  message_id:            string | null;
  answer:                string;
  answer_type:           ConversationAnswerType;
  meeting_profile:       MeetingProfile;
  grounding_status:      ConversationGroundingStatus;
  confidence:            number;
  sources:               MavisConversationSource[];
  evidence_items:        MavisEvidenceItem[];
  assumptions:           string[];
  uncertainty_notes:     string[];
  recommended_questions: string[];
  recommended_actions:   string[];
  speakable_later:       boolean;
  voice_safe:            boolean;
  requires_approval:     boolean;
  safety_notes:          string[];
  metadata:              Record<string, unknown>;
}

export interface MavisMeetingConversationMessage {
  id:                  string;
  org_id:              string;
  account_id:          string | null;
  opportunity_id:      string;
  meeting_session_id:  string | null;
  user_id:             string | null;
  role:                'user' | 'assistant' | 'system';
  question:            string | null;
  answer:              string | null;
  message_text:        string | null;
  meeting_profile:     MeetingProfile;
  answer_mode:         string;
  answer_type:         ConversationAnswerType | null;
  grounding_status:    ConversationGroundingStatus;
  confidence:          number | null;
  voice_safe:          boolean;
  requires_approval:   boolean;
  metadata:            Record<string, unknown>;
  created_at:          string;
}

// ─── Phase 4B — Controlled Voice MVP ──────────────────────────────────────────

export type VoiceOutputSourceType =
  | 'conversation_message'
  | 'response_draft'
  | 'custom_text'
  | 'closeout_questions';

export type VoiceApprovalStatus = 'pending' | 'approved' | 'rejected' | 'cancelled';

export type VoiceSpokenStatus =
  | 'pending'
  | 'queued'
  | 'speaking'
  | 'completed'
  | 'interrupted'
  | 'cancelled'
  | 'failed';

// Actions accepted by mavis-update-voice-state.
export type VoiceStateAction =
  | 'enable_internal_voice'
  | 'enable_approve_to_speak'
  | 'enable_called_upon'
  | 'mute'
  | 'unmute'
  | 'stop_speaking'
  | 'emergency_stop'
  | 'disable_voice'
  | 'reset_emergency_stop';

// A logged, intended MAVIS spoken output. Persisted before anything is ever
// spoken so there is a complete audit trail of what MAVIS was authorised to say.
export interface MavisMeetingVoiceOutput {
  id:                       string;
  org_id:                   string;
  account_id:               string | null;
  opportunity_id:           string;
  meeting_session_id:       string;
  conversation_message_id:  string | null;
  response_draft_id:        string | null;
  approved_by:              string | null;
  voice_mode:               string;
  output_text:              string;
  grounding_status:         ConversationGroundingStatus | string | null;
  knowledge_source_ids:     string[];
  knowledge_chunk_ids:      string[];
  evidence_items:           MavisEvidenceItem[];
  voice_safe:               boolean;
  requires_approval:        boolean;
  approval_status:          VoiceApprovalStatus;
  spoken_status:            VoiceSpokenStatus;
  started_at:               string | null;
  completed_at:             string | null;
  interrupted_at:           string | null;
  cancelled_at:             string | null;
  error_message:            string | null;
  metadata:                 Record<string, unknown>;
  created_at:               string;
}

export const VOICE_MODE_LABELS: Record<VoiceMode, string> = {
  disabled:         'Voice off',
  internal_prep:    'Internal prep voice',
  approve_to_speak: 'Approve to speak',
  called_upon:      'Called-upon',
};

export const VOICE_STATUS_LABELS: Record<VoiceStatus, string> = {
  idle:      'Idle',
  preparing: 'Preparing',
  speaking:  'Speaking',
  stopped:   'Stopped',
  muted:     'Muted',
  error:     'Error',
};

export const VOICE_APPROVAL_LABELS: Record<VoiceApprovalStatus, string> = {
  pending:   'Awaiting approval',
  approved:  'Approved',
  rejected:  'Rejected',
  cancelled: 'Cancelled',
};

export const VOICE_SPOKEN_LABELS: Record<VoiceSpokenStatus, string> = {
  pending:     'Not spoken',
  queued:      'Queued',
  speaking:    'Speaking',
  completed:   'Spoken',
  interrupted: 'Interrupted',
  cancelled:   'Cancelled',
  failed:      'Failed',
};
